import { useState } from 'react';
import { StatusBar, Modal, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './GlobalItemTaxRateEditModal.module.scss';
import { updateGlobalItemDetails } from 'src/api/updateGlobalItemDetails';
import { GlobalItemTaxRateForm } from 'src/components/CreateGlobalItemWizard/GlobalItemTaxRateForm/GlobalItemTaxRateForm';
import { IGlobalItem } from '../CreateGlobalItemWizard/types';
import { IUpdateDetailsRequestData } from 'src/api/updateGlobalItemDetails/types';
import chefPlating from 'src/assets/images/chef-plating.png';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import { FormVariant } from '../../types/menuItemForm.types';
import { IMenuItem } from 'src/types/menu.types';
import { IAggregator } from 'src/types/aggregator.types';

interface IGlobalItemTaxRateEditModal {
  currentItem: IMenuItem;
  aggregators: IAggregator[];
  onModalClose: () => void;
  isModalOpen: boolean;
}

const GlobalItemTaxForm = ({ onModalClose, currentItem }: Omit<IGlobalItemTaxRateEditModal, 'isModalOpen'>) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onEdit = async (data: Partial<IGlobalItem>, isDirty: boolean) => {
    clearError();
    try {
      if (isDirty) {
        setIsSubmitting(true);
        const requestData: IUpdateDetailsRequestData = {
          tax_rate: parseFloat(data.tax_rate),
          deposit_return_scheme: data.deposit_return_scheme !== null ? parseFloat(data.deposit_return_scheme) : null,
        };

        await updateGlobalItemDetails(currentItem.itemId, {
          data: JSON.stringify(requestData),
        });

        const queryKey = [QueryKeys.item, currentItem.itemId];
        await queryClient.invalidateQueries(queryKey);

        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: `You have successfully updated tax rate of "${currentItem.name}".`,
        });
      }
      onModalClose();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst updating tax rate of a product',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <GlobalItemTaxRateForm
        variant={FormVariant.UPDATE}
        item={{
          tax_rate: currentItem.tax_rate ? `${currentItem.tax_rate}` : '',
          deposit_return_scheme:
            currentItem.deposit_return_scheme === null ? null : currentItem.deposit_return_scheme.toString(),
        }}
        isSubmitting={isSubmitting}
        onSubmitItem={onEdit}
        onCloseOrBack={onModalClose}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </>
  );
};

const GlobalItemTaxRateEditModal = ({
  isModalOpen,
  onModalClose,
  aggregators,
  currentItem,
}: IGlobalItemTaxRateEditModal) => (
  <Modal open={isModalOpen} closeModal={onModalClose} image={chefPlating} imageAlt="chef">
    <GlobalItemTaxForm onModalClose={onModalClose} aggregators={aggregators} currentItem={currentItem} />
  </Modal>
);
export default GlobalItemTaxRateEditModal;
